/**
 * Cookie API with GDPR compliant preference level capabilities
 *
 * @author    Evan Cooper <evcooper@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */
/* eslint-disable consistent-return */
import {setCookie, getCookie, removeCookie} from '../core_cookies';
import {cookieBlocker} from '../cookie_blocker';
import {
  getIsFieldValidSubcookieOfCookieKey,
  cookiePreferenceLevelTypes,
  expireCookie,
  logCookiesFailedToExpire,
  logUnregisteredCookies,
  logProhibitedCookies,
  getAllowedCookies,
  COOKIE_REGISTRY_ERROR,
  SUB_COOKIE_REGISTRY_ERROR,
  USER_PREFERENCES_ERROR,
  FILTERING_ERROR,
  LOGGING_ERROR,
} from '../cookie_utils';
import Cookies from 'js-cookie';

const {NECESSARY} = cookiePreferenceLevelTypes;
const POST_EXPIRE_COOKIE_VALUE = '1';
const DEFAULT_LOGGER = {warn() {}, info() {}};

/**
 * User Preference Type Keys
 * @typedef {"NECESSARY" | "PERSONALIZATION" | "ANALYTICS" | "ADVERTISING" | "ADMINISTRATIVE"} UserPreferenceTypeKeys
 */

const isCookieTypeAllowed = (userPreferences, cookiePreferenceTypes) => {
  return userPreferences.some((preferenceLevels) =>
    cookiePreferenceTypes.includes(preferenceLevels)
  );
};

/**
 * createCookieApi
 *
 * @description Creates a cookie API capable of handling user preferences
 * @param {object} object - An object
 * @param {object} object.categoryData - Contains the key initialUserPreferences, which has a value of a user preferences object
 * @param {object} object.cookieRegistryData - Contains cookieRegistry, an object with keys of
 * cookie name strings, each with a value of a cookie preference level type string
 * @param {object} object.subcookieRegistryData - Contains subCookieRegistry, an object with keys
 * of cookie name strings, each with a value of an array populated with subcookie name strings
 * @param {boolean} object.isCookieFilteringEnabled - When enabled, cookie filtering is on. When disabled,
 * allow all cookie operations.
 * @param {boolean} object.shouldLogCookieData - When enabled, allow calls to the logging system. When disabled
 * all calls to the logger will be skipped.
 * @param {boolean} object.shouldLogCookieMetaData - When enabled, allows calls to log meta data on every set cookie call.
 * @param {boolean} object.shouldEnableGlobalCookieBlocking - When enabled, turns on the cookie blocker system
 * @param {function} object.cookieBlockerFn - Dependency injecting the cookieBlocker function for testing purposes
 * @param {object} object.logger - a logger instance
 */
export function createCookieApi({
  categoryData,
  cookieRegistryData,
  subcookieRegistryData,
  isCookieFilteringEnabled,
  shouldLogCookieData,
  shouldLogCookieMetaData,
  logger = DEFAULT_LOGGER,
  shouldEnableGlobalCookieBlocking = false,
  cookieBlockerFn = cookieBlocker,
}) {
  // Creating some internal variables
  const {cookieRegistry} = cookieRegistryData;
  const {subcookieRegistry} = subcookieRegistryData;
  const {initialUserPreferences} = categoryData;

  if (!cookieRegistry) {
    throw COOKIE_REGISTRY_ERROR;
  }
  if (!subcookieRegistry) {
    throw SUB_COOKIE_REGISTRY_ERROR;
  }
  if (!initialUserPreferences) {
    throw USER_PREFERENCES_ERROR;
  }
  if (isCookieFilteringEnabled == null) {
    throw FILTERING_ERROR;
  }
  if (shouldLogCookieData == null) {
    throw LOGGING_ERROR;
  }

  // Creating some internal state
  const cookieCollection = new Map();
  const userPreferences = {...initialUserPreferences};
  const getUserPreferences = () => {
    return userPreferences;
  };

  const getCookieTypes = (cookieName) => {
    const preferenceType = cookieRegistry[cookieName];
    if (typeof preferenceType === 'string') {
      return [preferenceType];
    }
    return preferenceType || [];
  };

  /**
   * Update the locally stored user preferences object
   * @param {object} newUserPreferences - keys: preference types, values: booleans
   */
  const updateUserPreferences = (newUserPreferences) => {
    Object.assign(userPreferences, newUserPreferences);
    removeInvalidCookies();
    if (shouldEnableGlobalCookieBlocking) {
      cookieBlockerFn({
        allowList: getAllowedCookies(getUserPreferences(), cookieRegistry),
        logger,
      });
    }
    retryCollectedCookies();
    return userPreferences;
  };

  /**
   * getSelectedPreferenceLevels
   * @returns {array} - Array of selected user preference types, aka [NECESSARY, ADVERTISING]
   */
  const getSelectedPreferenceLevels = (preferences = getUserPreferences()) => {
    return Object.keys(preferences).filter(
      (preferenceType) => preferences[preferenceType]
    );
  };

  /**
   * isPreferenceTypeCompliant
   *
   * @description Given a preference type ('NECESSARY', 'ANALYTICS', etc) returns true if
   * filtering is not active or if filtering is active and the preference type you pass is
   * included in the selected user preferences.
   * Throws an error if you do not pass a preference type or pass in a wrong one.
   * @param {UserPreferenceTypeKeys} preferenceType - user preference type you want to check for
   */
  const isPreferenceTypeCompliant = (preferenceType) => {
    if (!preferenceType) {
      throw new Error(
        'Please provide a preference type to isPreferenceTypeCompliant'
      );
    }
    if (!Object.keys(cookiePreferenceLevelTypes).includes(preferenceType)) {
      throw new Error(
        'Please provide a valid preference type to isPreferenceTypeCompliant'
      );
    }
    if (!isCookieFilteringEnabled) {
      return true;
    }
    return getSelectedPreferenceLevels().includes(preferenceType);
  };

  /**
   * attemptSetCookie
   *
   * @description Sets the cookie if user has accepted the cookie preference type. Otherwise,
   * adds the cookie call to a Map to be re-tried later if preferences change
   * @param {string} key - name of the cookie to set
   * @param {string} value - value of the cookie
   * @param {object} options - encompasses further options you can pass to the core setCookie function.
   * For more information, see https://docs.csnzoo.com/frontend/javascript/core-library-reference/cookie/#library-reference
   */
  const attemptSetCookie = (key, value, options = {}) => {
    const cookieTypes = getCookieTypes(key);

    if (shouldLogCookieData && shouldLogCookieMetaData) {
      logger.info({
        name: 'Set Cookie Occurrence FC',
        message: 'Set Cookie Occurrence FC',
        data: {
          cookieName: key,
          subcookieField: options.field || '',
          cookieValue: value,
          domain: options.domain || '',
          path: options.path || '/',
          expires: options.expires || 'Session',
        },
      });
    }

    // If filtering is not active, we want to guarantee
    // we set the cookie
    if (!isCookieFilteringEnabled) {
      return setCookie(key, value, options);
    }

    // If the cookie isn't in the registry, log the cookie
    if (!cookieTypes.length && shouldLogCookieData) {
      logger.warn({
        name: 'Unregistered Cookie',
        message: `The cookie "${key}" is not in our registry. Reach out to #sf-cookie-compliance`,
        data: {
          cookieName: key,
          cookieValue: value,
          actionType: 'setCookie',
          options,
        },
      });
      return;
    }

    const {field} = options;
    // If field was specified but the field isn't in the subcookie registry under the specified cookie key, log the subcookie field
    if (
      field &&
      !getIsFieldValidSubcookieOfCookieKey(field, key, subcookieRegistry) &&
      shouldLogCookieData
    ) {
      logger.warn({
        name: 'Unregistered Subcookie',
        message: `The subcookie field "${field}" is not in our registry. Reach out to #sf-cookie-compliance`,
        data: {
          cookieName: key,
          cookieValue: value,
          subcookieField: field,
          actionType: 'setSubcookie',
          options,
        },
      });
      return;
    }

    // If it's a functional cookie, we set it every time
    if (cookieTypes.includes(NECESSARY)) {
      return setCookie(key, value, options);
    }

    // If the cookie preference level is not accepted by the customer,
    // add the cookie request to local memory in case they change preferences
    if (!isCookieTypeAllowed(getSelectedPreferenceLevels(), cookieTypes)) {
      return cookieCollection.set(
        key,
        Object.keys(options).length === 0 ? [key, value] : [key, value, options]
      );
    }

    // If the cookie preference type is included in the user's preference
    // types, than set the cookie as normal
    setCookie(key, value, options);
  };

  /**
   * attemptGetCookie
   *
   * @description Returns a cookie if the purpose provided is included in the
   * user's selected preference levels. Always returns the cookie if it is a functional cookie.
   *
   * @param {string} key - name of the cookie to retrieve
   * @param {UserPreferenceTypeKeys} purpose - user preference type for why you are retrieving this cookie
   * @param {object} options - encompasses further options you can pass to the core getCookie function.
   * For more information, see https://docs.csnzoo.com/frontend/javascript/core-library-reference/cookie/#library-reference
   */
  const attemptGetCookie = (key, options = {}) => {
    const cookieTypes = getCookieTypes(key);

    // Skip all checks if isCookieFilteringEnabled is false
    if (!isCookieFilteringEnabled) {
      return getCookie(key, options);
    }

    // If the cookie isn't in the registry, log the cookie
    if (!cookieTypes.length && shouldLogCookieData) {
      logger.warn({
        name: 'Unregistered Cookie',
        message: `The cookie "${key}" is not in our registry. Reach out to #sf-cookie-compliance`,
        data: {
          cookieName: key,
          actionType: 'getCookie',
        },
      });
      return;
    }

    const {field} = options;
    // If field was specified but the field isn't in the subcookie registry under the specified cookie key, log the subcookie field
    if (
      field &&
      !getIsFieldValidSubcookieOfCookieKey(field, key, subcookieRegistry) &&
      shouldLogCookieData
    ) {
      logger.warn({
        name: 'Unregistered Subcookie',
        message: `The subcookie field "${field}" is not in our registry. Reach out to #sf-cookie-compliance`,
        data: {
          cookieName: key,
          subcookieField: field,
          actionType: 'getSubcookie',
        },
      });
      return;
    }

    // If its a necessary cookie we get it every time
    if (cookieTypes.includes(NECESSARY)) {
      return getCookie(key, options);
    }

    // If the cookie preference level is not accepted by the customer,
    // exit early
    if (!isCookieTypeAllowed(getSelectedPreferenceLevels(), cookieTypes)) {
      return;
    }

    return getCookie(key, options);
  };

  /**
   * @description Clears out the cookieCollection Map, attempting to set each
   * stored cookie again. May result in some or all of the cookies returning
   * to the cookieCollection Map
   */
  const retryCollectedCookies = () => {
    const allCookies = Array.from(cookieCollection.values());
    cookieCollection.clear();
    // retry each cookie, which may result in them back in the Map
    // for another retry later
    allCookies.forEach((cookieArgument) => {
      attemptSetCookie(...cookieArgument);
    });
  };

  /**
   * @description Iterates through all of the user's cookies, removing any with a preference type
   * that doesn't match the user's selected preference level types, then re-adding to the collection Map
   */
  const removeInvalidCookies = () => {
    const selectedPreferenceLevels = getSelectedPreferenceLevels();
    const cookies = Object.keys(Cookies.get());
    // object used here for O(1) indexing
    const expiredCookies = {};
    cookies.forEach((cookieName) => {
      if (
        !isCookieTypeAllowed(
          selectedPreferenceLevels,
          getCookieTypes(cookieName)
        )
      ) {
        expiredCookies[cookieName] = true;
        const cookieValue = Cookies.get(cookieName);
        expireCookie(cookieName, false);
        // check if we've expired the cookie,
        // if not, expire with paths
        // undefined is used here to avoid cases where the cookie value is falsy (i.e, 0, false, "", etc.)
        if (typeof getCookie(cookieName) !== 'undefined') {
          expireCookie(cookieName, true);
        }
        cookieCollection.set(cookieName, [cookieName, cookieValue]);
      }
    });

    if (shouldLogCookieData) {
      logCookiesFailedToExpire(expiredCookies, logger);
    }

    // set a cookie to signify to the backend that we cleared cookies
    if (!attemptGetCookie('cc_post_exp')) {
      attemptSetCookie('cc_post_exp', POST_EXPIRE_COOKIE_VALUE, {expires: 7});
    }
  };

  /**
   * @description Iterates through all of the user's cookies, noting which ones to log.
   * This will log cookies that are not in the taxonomy, and cookies that violate user preferences.
   *
   * @param {bool} isPostExpire - boolean indicating if we are running an audit after the first expiration attempt
   */
  const auditCookies = (isPostExpire) => {
    // cookies that are not in the registry
    const unregisteredCookies = {};

    // cookies that violate user preferences
    const prohibitedCookies = {};

    for (const [cookieName, cookieValue] of Object.entries(Cookies.get())) {
      if (!cookieRegistry[cookieName]) {
        unregisteredCookies[cookieName] = cookieValue;
      } else if (
        !isCookieTypeAllowed(
          getSelectedPreferenceLevels(),
          getCookieTypes(cookieName)
        )
      ) {
        prohibitedCookies[cookieName] = cookieValue;
      }
    }
    if (shouldLogCookieData) {
      logUnregisteredCookies(unregisteredCookies, logger, isPostExpire);
      logProhibitedCookies(prohibitedCookies, logger, isPostExpire);
    }
  };

  if (typeof window !== 'undefined' && isCookieFilteringEnabled) {
    // Cleans the user's cookies when they first enter into this library
    auditCookies(attemptGetCookie('cc_post_exp'));
    removeInvalidCookies();
    // Creates the cookieBlocker
    if (shouldEnableGlobalCookieBlocking) {
      cookieBlockerFn({
        allowList: getAllowedCookies(getUserPreferences(), cookieRegistry),
        logger,
      });
    }
  }

  return {
    setCookie: attemptSetCookie,
    getCookie: attemptGetCookie,
    removeCookie,
    cookieCollection,
    getSelectedPreferenceLevels,
    updateUserPreferences,
    cookiePreferenceLevelTypes,
    isPreferenceTypeCompliant,
  };
}
